import { bcDeadlines, bcTemplate, notificationComplete } from "./bc";
import { isLateEntry } from "./time";
import {
  Incident,
  IncidentDocumentation,
  IncidentNotification,
  IncidentType,
  NotificationParty,
  Province,
  ReasonableGroundsDecision
} from "./types";
import { hasText } from "./text";

export const FORM_SECTIONS = ["respond", "assess", "notify", "document", "review"] as const;

export type FormSectionId = (typeof FORM_SECTIONS)[number];

export interface SectionProgress {
  id: FormSectionId;
  required: number;
  done: number;
  remaining: number;
  complete: boolean;
  missing: string[];
}

export interface DocumentationProgressInput {
  type: IncidentType;
  province: Province;
  documentation: IncidentDocumentation;
  notifications: IncidentNotification[];
  reasonable_grounds_decision: ReasonableGroundsDecision;
  confirmed_at?: string;
  alert_at?: string;
  bc_template?: Incident["bc_template"];
  site_jurisdiction?: Incident["site_jurisdiction"];
  province_confirmed?: boolean;
  room?: string;
  location?: string;
  actor_user_id?: string;
  /** When set, late-entry progress uses lock/attestation time instead of “now”. */
  locked_at?: string;
}

interface Check {
  id: string;
  done: boolean;
}

function filled(value: unknown): boolean {
  return hasText(value);
}

function push(items: Check[], id: string, done: boolean): void {
  items.push({ id, done });
}

/** Entry time for late-entry section checks: finalized records use lock time, drafts use now. */
function documentationEntryAt(input: DocumentationProgressInput): Date {
  const at = input.locked_at || input.documentation?.attestation_at;
  return at ? new Date(at) : new Date();
}

function notificationDone(notifications: IncidentNotification[], party: NotificationParty): boolean {
  return notifications.some(
    (row) => row.party === party && row.done && Boolean(row.at) && hasText(row.person_name)
  );
}

/** Parties the nurse must record before finalize. Other rows are reminders. */
function bcIncident(input: DocumentationProgressInput): Incident {
  return {
    id: "progress",
    facility_id: "",
    facility_name: "",
    alert_id: "",
    type: input.type,
    status: "in_documentation",
    province: "BC",
    province_confirmed: input.province_confirmed,
    bc_template: input.bc_template,
    site_jurisdiction: input.site_jurisdiction,
    room: input.room,
    alert_at: input.alert_at || "",
    alert_summary: "",
    confirmed_at: input.confirmed_at,
    created_at: input.confirmed_at || "",
    notified_staff: [],
    documentation: input.documentation,
    reasonable_grounds_decision: input.reasonable_grounds_decision,
    notifications: input.notifications,
    events: [],
    amendments: []
  };
}

export function requiredNotificationParties(input: DocumentationProgressInput): NotificationParty[] {
  if (input.province === "BC") {
    return bcDeadlines(bcIncident(input))
      .filter((row) => row.required)
      .map((row) => row.party as NotificationParty);
  }
  const parties: NotificationParty[] = ["charge_nurse"];
  const type = input.type;
  const doc = input.documentation ?? {};
  const decision = input.reasonable_grounds_decision?.decision;
  if (type === "fall") parties.push("physician", "sdm_family");
  if (type === "unsafe_interaction") {
    parties.push("doc");
    if (doc.sdm_responsible !== "yes") parties.push("sdm_family");
  }
  if (type === "wandering_zone") parties.push("sdm_family");
  if (decision === "yes" && doc.criminal_offence === "yes") parties.push("police");
  return parties;
}

function tally(id: FormSectionId, items: Check[]): SectionProgress {
  const missing = items.filter((item) => !item.done).map((item) => item.id);
  return {
    id,
    required: items.length,
    done: items.length - missing.length,
    remaining: missing.length,
    complete: missing.length === 0,
    missing
  };
}

/**
 * Required fields in nursing order. NeuraVue confirmations count in Respond,
 * because that is where the nurse confirms the pre-filled summary.
 */
export function documentationSections(input: DocumentationProgressInput): SectionProgress[] {
  if (input.province === "BC") return bcDocumentationSections(input);
  const doc = input.documentation ?? {};

  const type = input.type;
  const decision = input.reasonable_grounds_decision?.decision;

  const respond: Check[] = [];
  const neuravueConfirmed =
    Boolean(doc.resident_confirmed) &&
    Boolean(doc.event_time_confirmed) &&
    Boolean(doc.discovered_at_confirmed) &&
    Boolean(doc.location_confirmed) &&
    Boolean(doc.responders_confirmed) &&
    Boolean(doc.prefilled_confirmed) &&
    Boolean(doc.critical_incident_category_confirmed) &&
    (type !== "fall" || Boolean(doc.post_fall_instrument_confirmed));
  push(respond, "neuravue_confirmed", neuravueConfirmed);
  push(respond, "witnessed", Boolean(doc.witnessed));
  if (doc.witnessed === "yes") push(respond, "witnessed_by", filled(doc.witnessed_by));
  push(respond, "care_given", filled(doc.care_given));
  if (type === "unsafe_interaction" || type === "responsive_behaviour") {
    push(respond, "safety_measures", filled(doc.safety_measures));
  }
  if (type === "responsive_behaviour") push(respond, "intervention", filled(doc.intervention));
  push(respond, "hospital_transfer", Boolean(doc.hospital_transfer));
  if (doc.hospital_transfer === "yes") push(respond, "hospital_name", filled(doc.hospital_name));

  const assess: Check[] = [];
  if (type === "fall" || type === "unsafe_interaction") {
    push(assess, "consciousness", filled(doc.consciousness));
    push(assess, "pain", filled(doc.pain));
    push(assess, "injuries", doc.injuries_none === true || filled(doc.injuries));
  }
  if (type === "fall") {
    push(assess, "vitals", filled(doc.vitals));
    push(assess, "post_fall_instrument", filled(doc.post_fall_instrument));
    push(assess, "post_fall_completed_at", Boolean(doc.post_fall_completed_at));
  }
  if (type === "unsafe_interaction" || type === "responsive_behaviour") {
    push(assess, "distress", filled(doc.distress));
  }
  if (type === "responsive_behaviour") push(assess, "triggers", filled(doc.triggers));
  if (type === "wandering_zone") {
    push(assess, "missing_last_seen", Boolean(doc.missing_last_seen));
    push(assess, "missing_found_time", Boolean(doc.missing_found_time));
    push(assess, "missing_found_location", filled(doc.missing_found_location));
  }

  const notify = requiredNotificationParties(input).map((party) => ({
    id: `notify_${party}`,
    done: notificationDone(input.notifications, party)
  }));

  const documentItems: Check[] = [];
  push(documentItems, "observed", filled(doc.observed));
  push(documentItems, "persons_present", filled(doc.persons_present));
  if (type !== "other") push(documentItems, "leading_events", filled(doc.leading_events));
  if (type === "unsafe_interaction") {
    push(documentItems, "other_residents", filled(doc.other_residents));
    push(documentItems, "resident_statement", filled(doc.resident_statement));
    if (decision === "yes") push(documentItems, "investigation_lead", filled(doc.investigation_lead));
  }
  push(documentItems, "immediate_prevention", filled(doc.immediate_prevention));
  push(documentItems, "long_term_actions", filled(doc.long_term_actions));
  if (type === "fall" || type === "responsive_behaviour" || type === "wandering_zone") {
    push(documentItems, "care_plan_updated", filled(doc.care_plan_updated));
  }
  push(documentItems, "outcome", filled(doc.outcome));

  const review: Check[] = [];
  if (decision === "unsure") push(review, "reasonable_grounds", false);
  else push(review, "reasonable_grounds", Boolean(decision));
  if (decision === "yes") push(review, "suspicion", filled(input.reasonable_grounds_decision?.suspicion));
  if (input.province === "QC") {
    push(review, "qc_ah223", Boolean(doc.qc_ah223_confirmed) && Boolean(doc.qc_ah223_required));
  }
  if (isLateEntry(doc.event_time || doc.discovered_at, documentationEntryAt(input))) {
    push(review, "late_entry_reason", filled(doc.late_entry_reason));
  }
  push(review, "attestation_accepted", Boolean(doc.attestation_accepted));
  push(review, "attestation_name", filled(doc.attestation_name));
  push(review, "attestation_designation", filled(doc.attestation_designation));

  return [
    tally("respond", respond),
    tally("assess", assess),
    tally("notify", notify),
    tally("document", documentItems),
    tally("review", review)
  ];
}

function filledText(value: string | undefined): boolean {
  return Boolean(value && value.trim());
}

function bcDocumentationSections(input: DocumentationProgressInput): SectionProgress[] {
  const doc = input.documentation;
  const type = input.type;
  const behaviour = type === "unsafe_interaction" || type === "responsive_behaviour";
  const incident = bcIncident(input);
  const namesOff = input.site_jurisdiction?.residentNamesAllowed === false;
  const respond: Check[] = [];
  push(
    respond,
    "neuravue_confirmed",
    Boolean(doc.event_time_confirmed) &&
      Boolean(doc.discovered_at_confirmed) &&
      Boolean(doc.location_confirmed) &&
      Boolean(doc.prefilled_confirmed) &&
      (namesOff || Boolean(doc.resident_confirmed)) &&
      Boolean(doc.event_time) &&
      Boolean(doc.discovered_at) &&
      filledText(doc.location || input.location || input.room)
  );
  if (namesOff) {
    push(
      respond,
      "resident_identifier",
      filledText(doc.resident_room) || filledText(doc.pcc_patient_id) || filledText(input.room)
    );
  }
  push(respond, "witnessed", Boolean(doc.witnessed));
  if (doc.witnessed === "yes") {
    push(respond, "witnessed_by", filledText(doc.witnessed_by));
    push(respond, "witnessed_title", filledText(doc.witnessed_title));
  }
  push(respond, "care_given", filledText(doc.care_given));
  push(respond, "emergency_care", doc.emergency_care === "yes" || doc.emergency_care === "no");
  push(respond, "hospital_transfer", Boolean(doc.hospital_transfer));
  if (doc.hospital_transfer === "yes") {
    push(respond, "hospital_name", filledText(doc.hospital_name));
    push(respond, "hospital_time", Boolean(doc.hospital_time));
  }
  if (doc.other_party_kind === "staff") {
    push(
      respond,
      "staff_removed_from_care",
      doc.staff_removed_from_care === "yes" || doc.staff_removed_from_care === "no"
    );
  }

  const assess: Check[] = [];
  push(assess, "consciousness", filledText(doc.consciousness));
  push(assess, "pain", filledText(doc.pain));
  push(assess, "injuries", doc.injuries_none === true || filledText(doc.injuries));
  push(assess, "vitals", filledText(doc.vitals) || filledText(doc.vitals_not_done_reason));
  if (type === "fall") {
    push(assess, "head_strike", doc.head_strike === "yes" || doc.head_strike === "no" || doc.head_strike === "unknown");
    if (doc.head_strike === "yes" || doc.head_strike === "unknown") {
      push(assess, "neuro_checks_started", doc.neuro_checks_started === "yes" || doc.neuro_checks_started === "no");
      const protocol = input.site_jurisdiction?.postFallProtocol;
      const onFile = Boolean(protocol?.name && protocol?.version);
      if (doc.neuro_checks_started === "yes") {
        push(
          assess,
          "post_fall_protocol",
          onFile &&
            doc.post_fall_protocol_name === protocol?.name &&
            doc.post_fall_protocol_version === protocol?.version
        );
      }
      if (doc.neuro_checks_started === "no") {
        push(assess, "neuro_checks_reason", filledText(doc.neuro_checks_reason));
      }
    }
    push(assess, "environment_checked", doc.environment_checked === "yes" || doc.environment_checked === "no");
    push(assess, "environment_note", filledText(doc.environment_note));
  }
  if (behaviour) {
    push(assess, "other_party_kind", Boolean(doc.other_party_kind));
    if (doc.other_party_kind === "resident") {
      push(assess, "in_care_plan", doc.in_care_plan === "yes" || doc.in_care_plan === "no");
      push(assess, "other_resident_room", filledText(doc.other_resident_room));
    }
    if (doc.other_party_kind === "staff" || doc.other_party_kind === "visitor" || doc.other_party_kind === "unknown") {
      push(
        assess,
        "suspected_abuse",
        doc.suspected_abuse === "yes" || doc.suspected_abuse === "no" || doc.suspected_abuse === "unsure"
      );
      if (doc.suspected_abuse === "yes" || doc.suspected_abuse === "unsure") {
        push(assess, "conduct", Boolean(doc.conduct) && doc.conduct !== "none");
      }
    }
    push(assess, "first_aid_needed", doc.first_aid_needed === "yes" || doc.first_aid_needed === "no");
    push(assess, "witness_residents", filledText(doc.witness_residents));
    push(assess, "distress", filledText(doc.distress));
    push(assess, "intervention", filledText(doc.intervention));
    push(assess, "restraint_used", doc.restraint_used === "yes" || doc.restraint_used === "no");
    push(
      assess,
      "criminal_offence",
      doc.criminal_offence === "yes" || doc.criminal_offence === "no" || doc.criminal_offence === "unsure"
    );
    push(assess, "evidence_preserved", doc.evidence_preserved === "yes" || doc.evidence_preserved === "no");
  }

  const incidentForNotify = bcIncident(input);
  const notify = requiredNotificationParties(input).map((party) => ({
    id: `notify_${party}`,
    done: notificationComplete(incidentForNotify, party)
  }));
  if (
    input.actor_user_id &&
    input.notifications.some(
      (row) => row.done && row.recorded_by && String(row.recorded_by) !== String(input.actor_user_id)
    )
  ) {
    notify.push({ id: "notify_actor", done: false });
  }

  const documentItems: Check[] = [];
  push(documentItems, "observed", filledText(doc.observed));
  push(documentItems, "resident_statement", filledText(doc.resident_statement));
  push(documentItems, "persons_present", filledText(doc.persons_present));
  push(documentItems, "leading_events", filledText(doc.leading_events));
  if (type === "fall") push(documentItems, "contributing_factors", filledText(doc.contributing_factors));
  push(documentItems, "corrective_actions", filledText(doc.corrective_actions));
  if (type === "fall") push(documentItems, "care_plan_updated", filledText(doc.care_plan_updated));
  push(documentItems, "outcome", filledText(doc.outcome));
  const suggestionReportable = bcDeadlines(incident).some((row) => row.party === "bc_mho" || row.party === "bc_alr");
  if (suggestionReportable) push(documentItems, "bc_category", Boolean(doc.bc_category_confirmed && doc.bc_category));
  else push(documentItems, "facility_record_confirmed", Boolean(doc.facility_record_confirmed));
  if (doc.aga_reported === "yes") {
    push(documentItems, "aga_reference", Boolean(doc.aga_reported_at) && filledText(doc.aga_reference));
  }

  const review: Check[] = [];
  push(review, "province_confirmed", Boolean(input.province_confirmed));
  if (input.site_jurisdiction && input.site_jurisdiction.aiDocPolicyAck !== true) {
    push(review, "ai_policy", false);
  }
  if (isLateEntry(doc.event_time || doc.discovered_at, documentationEntryAt(input))) {
    push(review, "late_entry_reason", filledText(doc.late_entry_reason));
  }
  push(review, "attestation_accepted", Boolean(doc.attestation_accepted));
  push(review, "attestation_name", filledText(doc.attestation_name));
  push(
    review,
    "attestation_designation",
    ["RN", "LPN", "RPN", "NP", "HCA"].includes((doc.attestation_designation || "").toUpperCase())
  );
  if (!bcTemplate(incident) && input.confirmed_at) push(review, "bc_template", false);

  return [
    tally("respond", respond),
    tally("assess", assess),
    tally("notify", notify),
    tally("document", documentItems),
    tally("review", review)
  ];
}

